import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import useToolStore, { TOOL_TYPES, hasToolPreview } from '../../store/useToolStore';
import Toolbar from '../Toolbar/Toolbar';
import { ToolControls, ToolPreview } from './ToolRouter';
import CodeEditor from '../CodeEditor/CodeEditor';
import ThreePanelLayout from '../Layout/ThreePanelLayout';
import TwoPanelLayout from '../Layout/TwoPanelLayout';
import SEO from '../SEO/SEO';
import { getSEOData } from '../../data/seoData';
import StoryboardFileList from './StoryboardConverter/StoryboardFileList';
import { getToolSlugByPath } from '../../data/toolSlugs';
import { TOOL_SLUGS } from '../../data/toolSlugs';
import { getToolContentData } from '../../data/toolContentData';
import { getToolCategory, getToolsByCategory, TOOL_CATEGORIES } from '../../store/useToolStore';

const ToolPage = () => {
  const location = useLocation();
  const toolSlug = getToolSlugByPath(location.pathname);
  const toolStoreType = toolSlug?.toolType || TOOL_TYPES.LOADER;
  const setActiveTool = useToolStore((state) => state.setActiveTool);
  const generatedCode = useToolStore((state) => state.generatedCode);
  const generatedFiles = useToolStore((state) => state.generatedFiles);
  const selectedFileIndex = useToolStore((state) => state.selectedFileIndex);
  const previewInCodeWindow = useToolStore((state) => state.previewInCodeWindow);
  const previewFileIndex = useToolStore((state) => state.previewFileIndex);
  
  const [isToolbarVisible, setIsToolbarVisible] = useState(true);
  const lastScrollY = useRef(0);
  
  useEffect(() => {
    setActiveTool(toolStoreType);
  }, [toolStoreType, setActiveTool]);

  // Scroll-to-hide toolbar functionality
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollableContainers = document.querySelectorAll('.overflow-y-auto');
      
      let maxScroll = 0;
      scrollableContainers.forEach(container => {
        maxScroll = Math.max(maxScroll, container.scrollTop);
      });
      
      const totalScroll = currentScrollY + maxScroll;
      
      // Show toolbar when scrolling up or at top, hide when scrolling down
      if (totalScroll > lastScrollY.current && totalScroll > 50) {
        setIsToolbarVisible(false);
      } else {
        setIsToolbarVisible(true);
      }
      
      lastScrollY.current = totalScroll;
    };

    // Listen to scroll events on window and scrollable containers
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    const scrollableContainers = document.querySelectorAll('.overflow-y-auto');
    scrollableContainers.forEach(container => {
      container.addEventListener('scroll', handleScroll, { passive: true });
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      scrollableContainers.forEach(container => {
        container.removeEventListener('scroll', handleScroll);
      });
    };
  }, []);

  const seoKey = toolSlug?.seoKey || 'home';
  const seoData = getSEOData(seoKey);
  const contentData = getToolContentData(seoKey);
  const hasPreview = hasToolPreview(toolStoreType);
  const isStoryboardConverter = toolStoreType === TOOL_TYPES.STORYBOARD_CONVERTER;
  const hasMultipleFiles = isStoryboardConverter && generatedFiles && generatedFiles.length > 0;
  const category = getToolCategory(toolStoreType);

  const relatedTools = useMemo(() => {
    const categoryTools = getToolsByCategory(category)
      .filter((t) => t !== toolStoreType)
      .filter((t) => t !== TOOL_TYPES.STORYBOARD_CONVERTER);

    const mapped = categoryTools
      .map((toolType) => TOOL_SLUGS.find((s) => s.toolType === toolType))
      .filter(Boolean)
      .slice(0, 6);

    return mapped;
  }, [category, toolStoreType]);

  // Get the code to display
  const codeToDisplay = hasMultipleFiles 
    ? (generatedFiles[selectedFileIndex]?.content || '') 
    : generatedCode;
  
  // Get the file name to display
  const fileNameToDisplay = hasMultipleFiles 
    ? generatedFiles[selectedFileIndex]?.name 
    : null;
  
  // Get preview code for code window (if dual preview is enabled)
  const previewCodeForWindow = hasMultipleFiles && previewInCodeWindow && previewFileIndex !== null
    ? generatedFiles[previewFileIndex]?.content
    : null;

  // Render code panel for storyboard converter with file list
  const renderStoryboardCodePanel = () => {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
          {/* File List - Top on mobile, Left on desktop */}
          <div className="w-full lg:w-64 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 flex-shrink-0 h-48 lg:h-auto">
            <StoryboardFileList />
          </div>
          {/* Code Editor - Bottom on mobile, Right on desktop */}
          <div className="flex-1 min-h-0">
            <CodeEditor 
              code={codeToDisplay} 
              fileName={fileNameToDisplay}
              showPreview={previewInCodeWindow && previewFileIndex !== null}
              previewCode={previewCodeForWindow}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        image={seoData.image}
        jsonLd={seoData.jsonLd}
        type="website"
      />
      <div className="relative bg-white dark:bg-gray-900 transition-colors duration-200">
        {/* Toolbar Section - Fixed/Sticky with scroll-to-hide */}
        <div 
          className={`sticky top-0 z-20 bg-white dark:bg-gray-900 transition-transform duration-300 ${
            isToolbarVisible ? 'translate-y-0' : '-translate-y-full'
          }`}
        >
          <Toolbar />
        </div>

        {/* SEO Content Section (scrolls; keeps tool UI boxed below) */}
        {contentData && (
          <div className="border-b border-gray-200 dark:border-gray-800">
            <div className="max-w-[1000px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">
                {contentData.heading || seoData.title}
              </h1>
              <div className="mt-4 space-y-3 text-gray-700 dark:text-gray-300 leading-relaxed">
                {(contentData.intro || []).map((p, idx) => (
                  <p key={idx}>{p}</p>
                ))}
              </div>

              {!!(contentData.examples && contentData.examples.length) && (
                <div className="mt-8">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Examples (copy-paste)
                  </h2>
                  <div className="mt-4 space-y-4">
                    {contentData.examples.map((ex, idx) => (
                      <div
                        key={idx}
                        className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 overflow-hidden"
                      >
                        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                          <div className="text-sm font-semibold text-gray-900 dark:text-gray-200">
                            {ex.title}
                          </div>
                          <button
                            className="text-xs px-3 py-1.5 bg-gradient-primary hover:opacity-90 rounded-md text-white font-medium transition-all duration-200"
                            onClick={async (e) => {
                              try {
                                await navigator.clipboard.writeText(ex.code || '');
                                const btn = e.currentTarget;
                                const originalText = btn.textContent;
                                btn.textContent = 'Copied!';
                                btn.classList.add('bg-green-500');
                                setTimeout(() => {
                                  btn.textContent = originalText;
                                  btn.classList.remove('bg-green-500');
                                }, 1500);
                              } catch (err) {
                                console.error('Failed to copy:', err);
                              }
                            }}
                          >
                            Copy
                          </button>
                        </div>
                        <pre className="p-4 text-xs sm:text-sm overflow-x-auto text-gray-900 dark:text-gray-100">
                          <code>{ex.code}</code>
                        </pre>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!!(relatedTools && relatedTools.length) && (
                <div className="mt-10">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Related tools
                  </h2>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {relatedTools.map((t) => (
                      <Link
                        key={t.seoPath}
                        to={t.seoPath}
                        className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors px-4 py-3"
                      >
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          {getSEOData(t.seoKey).title}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          {category === TOOL_CATEGORIES.SWIFTUI ? 'SwiftUI tool' : 'Swift tool'}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Content Section - Boxed Layout with max-width 1400px, Sticky */}
        <div className="sticky top-[56px] z-10" style={{ height: 'calc(100vh - 56px)' }}>
          <div className="max-w-[1400px] mx-auto h-full">
            {hasPreview ? (
              <ThreePanelLayout
                leftPanel={<ToolControls />}
                middlePanel={<ToolPreview />}
                rightPanel={
                  isStoryboardConverter && hasMultipleFiles
                    ? renderStoryboardCodePanel()
                    : <CodeEditor 
                        code={codeToDisplay} 
                        fileName={fileNameToDisplay}
                        showPreview={previewInCodeWindow && previewFileIndex !== null}
                        previewCode={previewCodeForWindow}
                      />
                }
              />
            ) : (
              <TwoPanelLayout
                leftPanel={<ToolControls />}
                rightPanel={
                  isStoryboardConverter && hasMultipleFiles
                    ? renderStoryboardCodePanel()
                    : <CodeEditor 
                        code={codeToDisplay} 
                        fileName={fileNameToDisplay}
                        showPreview={previewInCodeWindow && previewFileIndex !== null}
                        previewCode={previewCodeForWindow}
                      />
                }
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ToolPage;

